import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit6Notes: UnitNotes = {
  unitId: "unit-6",
  unitName: "Unit 6: Inference for Categorical Data (Proportions)",
  examWeight: "12–15%",
  focus: "Using Sample Data to Make Inferences About Populations",
  sections: [
    {
      id: "topic-6-1",
      title: "Topic 6.1: Introducing Statistics: Why Be Normal?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Goal of Inference</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Statistical inference allows us to draw conclusions about a population parameter (such as a population proportion, <InlineMath math="p" />) based on a sample statistic (such as a sample proportion, <InlineMath math="\hat{p}" />).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Role of the Normal Distribution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To calculate probabilities involving sample proportions, we need a predictable model. We rely on the <strong>Sampling Distribution</strong> of <InlineMath math="\hat{p}" />.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>When sample sizes are large enough, the sampling distribution of <InlineMath math="\hat{p}" /> is approximately <strong>Normal</strong>.</li>
              <li>This allows us to use <InlineMath math="z" />-scores and the Standard Normal Table (Table A) to perform calculations.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Large Counts Condition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To justify using the Normal distribution for inference, we must verify that the expected number of successes and failures are both sufficiently large.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="font-semibold text-blue-900">Condition:</p>
              <BlockMath math="np \geq 10 \quad \text{and} \quad n(1-p) \geq 10" />
            </div>
            <ImportantNote>
              For confidence intervals, use <InlineMath math="\hat{p}" /> in place of <InlineMath math="p" />. For hypothesis tests, use the null parameter <InlineMath math="p_0" />.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-2",
      title: "Topic 6.2: Constructing a Confidence Interval for a Population Proportion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Point Estimates and Margins of Error</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Point Estimate" 
                definition="The statistic calculated from the sample (p̂) is our best guess for the true population parameter (p)."
              />
              <KeyTerm 
                term="Margin of Error (ME)" 
                definition="Indicates the precision of the estimate. It measures the maximum likely difference between the point estimate and the true parameter at a specific confidence level."
              />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="font-semibold text-slate-800 mb-2">Interval Formula:</p>
              <BlockMath math="\text{Statistic} \pm (\text{Critical Value}) \times (\text{Standard Error})" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The One-Sample z-Interval Formula</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\hat{p} \pm z^* \sqrt{\frac{\hat{p}(1-\hat{p})}{n}}" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="\hat{p}" />: Sample proportion</li>
              <li><InlineMath math="z^*" />: Critical value based on the confidence level (e.g., 1.96 for 95%)</li>
              <li><InlineMath math="\sqrt{\frac{\hat{p}(1-\hat{p})}{n}}" />: Standard Error (SE) of the statistic</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions for Inference (The "RIN" Check)</h3>
            <Mnemonic title="RIN" content="Random, Independence (10% Condition), Normal (Large Counts) — the conditions to check before inference." />
            <ol className="list-decimal pl-6 space-y-3 text-slate-700 mt-4">
              <li><strong>Random:</strong> The data comes from a well-designed random sample or randomized experiment.</li>
              <li><strong>Independence (10% Condition):</strong> If sampling without replacement, <InlineMath math="n \leq 0.10N" />. This ensures the standard deviation formula is accurate.</li>
              <li><strong>Normal (Large Counts):</strong> <InlineMath math="n\hat{p} \geq 10" /> and <InlineMath math="n(1-\hat{p}) \geq 10" />. This ensures the sampling distribution is approximately Normal.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding the Critical Value (<InlineMath math="z^*" />)</h3>
            <div className="bg-slate-100 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="font-semibold">90% Confidence</p>
                  <p><InlineMath math="z^* = 1.645" /></p>
                </div>
                <div>
                  <p className="font-semibold">95% Confidence</p>
                  <p><InlineMath math="z^* = 1.96" /></p>
                </div>
                <div>
                  <p className="font-semibold">99% Confidence</p>
                  <p><InlineMath math="z^* = 2.576" /></p>
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-2"><em>Method:</em> Use <code className="bg-slate-200 px-1 rounded">invNorm</code> on a calculator with the tail area (e.g., for 95%, the center is 0.95, so the tail is 0.025).</p>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-3",
      title: "Topic 6.3: Justifying a Claim Based on a Confidence Interval",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Confidence Interval</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="font-semibold text-green-900 mb-2">Template:</p>
              <p className="text-slate-700">"We are [C]% confident that the interval from [lower bound] to [upper bound] captures the true proportion of [context of the problem]."</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Confidence Level</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="font-semibold text-blue-900 mb-2">Template:</p>
              <p className="text-slate-700">"If we were to take many, many samples of the same size from this population and construct a confidence interval for each, about [C]% of those intervals would capture the true proportion of [context]."</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Making a Claim</h3>
            <ul className="list-disc pl-6 space-y-3 text-slate-700">
              <li><strong>Plausible Values:</strong> Any value <em>inside</em> the interval is a plausible value for the true parameter.</li>
              <li><strong>Rejecting a Claim:</strong> If a claimed value falls <em>outside</em> the interval, we have evidence to reject that claim at the <InlineMath math="\alpha = 1 - C" /> significance level.</li>
              <li><strong>Supporting a Claim:</strong> If the claimed value falls <em>inside</em> the interval, we do not have evidence to dispute it (though we generally don't "prove" it is true, just that it is plausible).</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-4",
      title: "Topic 6.4: Setting Up a Test for a Population Proportion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Hypotheses</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Hypothesis testing assesses the evidence provided by data against a null claim.
            </p>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Null Hypothesis (<InlineMath math="H_0" />)</h4>
                <p className="text-sm text-slate-700 mt-1">The statement of "no difference" or "status quo." Always includes an equal sign.</p>
                <div className="mt-2"><BlockMath math="H_0: p = p_0" /></div>
                <p className="text-sm text-slate-600">(where <InlineMath math="p_0" /> is the hypothesized value)</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Alternative Hypothesis (<InlineMath math="H_a" />)</h4>
                <p className="text-sm text-slate-700 mt-1">The claim we are trying to find evidence <em>for</em>.</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li>One-sided: <InlineMath math="H_a: p < p_0" /> or <InlineMath math="H_a: p > p_0" /></li>
                  <li>Two-sided: <InlineMath math="H_a: p \neq p_0" /></li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions for a Test</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The conditions are identical to Topic 6.2, with one major change:
            </p>
            <ImportantNote>
              <strong>Normal (Large Counts):</strong> Use the <em>null parameter</em> (<InlineMath math="p_0" />), not the sample statistic (<InlineMath math="\hat{p}" />).
              <div className="mt-2">Check: <InlineMath math="np_0 \geq 10" /> and <InlineMath math="n(1-p_0) \geq 10" /></div>
              <p className="mt-2"><em>Reasoning:</em> We assume <InlineMath math="H_0" /> is true when running the test, so we use the parameter from <InlineMath math="H_0" />.</p>
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-5",
      title: "Topic 6.5: Interpreting p-Values",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Test Statistic (<InlineMath math="z" />)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Measures how many standard deviations the sample statistic is from the null parameter.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="z = \frac{\text{Statistic} - \text{Null Parameter}}{\text{Standard Deviation}} = \frac{\hat{p} - p_0}{\sqrt{\frac{p_0(1-p_0)}{n}}}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The p-Value Definition</h3>
            <KeyTerm 
              term="p-Value" 
              definition="The probability of getting a sample statistic as extreme or more extreme than the one observed, assuming the null hypothesis is true."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the p-Value</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="font-semibold text-green-900 mb-2">Template:</p>
              <p className="text-slate-700">"Assuming the true population proportion is [value of <InlineMath math="p_0" />], there is a [p-value] probability of obtaining a sample proportion of [value of <InlineMath math="\hat{p}" />] or more extreme purely by chance."</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-6",
      title: "Topic 6.6: Concluding a Test for a Population Proportion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Comparing p-Value to Alpha (<InlineMath math="\alpha" />)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The significance level (<InlineMath math="\alpha" />) is the threshold for evidence (usually 0.05).
            </p>
            
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">If p-value <InlineMath math="< \alpha" /></h4>
                <p className="text-sm text-slate-700 mt-1">The results are unlikely to happen by chance.</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><strong>Decision:</strong> Reject <InlineMath math="H_0" />.</li>
                  <li><strong>Conclusion:</strong> There is convincing evidence for <InlineMath math="H_a" />.</li>
                </ul>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">If p-value <InlineMath math="> \alpha" /></h4>
                <p className="text-sm text-slate-700 mt-1">The results could easily happen by chance.</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><strong>Decision:</strong> Fail to reject <InlineMath math="H_0" />.</li>
                  <li><strong>Conclusion:</strong> There is <em>not</em> convincing evidence for <InlineMath math="H_a" />.</li>
                </ul>
              </div>
            </div>
          </section>

          <ImportantNote>
            Always write the conclusion in the context of the problem. Never say "we proved <InlineMath math="H_0" /> is true." Only say "we do not have evidence to reject it."
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-6-7",
      title: "Topic 6.7: Potential Errors When Performing Tests",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-bold text-red-900">Type I Error (False Positive)</h4>
              <KeyTerm 
                term="Definition" 
                definition="Rejecting H₀ when H₀ is actually true."
              />
              <p className="text-sm text-slate-700 mt-2"><strong>Probability:</strong> <InlineMath math="P(\text{Type I Error}) = \alpha" /> (Significance Level)</p>
              <p className="text-sm text-slate-600 mt-2"><strong>Consequence:</strong> We conclude there is an effect/difference when there actually isn't one.</p>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-bold text-amber-900">Type II Error (False Negative)</h4>
              <KeyTerm 
                term="Definition" 
                definition="Failing to reject H₀ when H₀ is actually false (and Hₐ is true)."
              />
              <p className="text-sm text-slate-700 mt-2"><strong>Probability:</strong> Denoted by <InlineMath math="\beta" /> (Beta)</p>
              <p className="text-sm text-slate-600 mt-2"><strong>Consequence:</strong> We miss finding an effect/difference that actually exists.</p>
            </div>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Power of a Test</h3>
            <KeyTerm 
              term="Power" 
              definition="The probability of correctly rejecting H₀ when Hₐ is true."
            />
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
              <BlockMath math="\text{Power} = 1 - \beta" />
            </div>
            <div className="mt-4">
              <h4 className="font-semibold text-slate-800 mb-2">How to Increase Power:</h4>
              <ol className="list-decimal pl-6 space-y-2 text-slate-700">
                <li>Increase sample size (<InlineMath math="n" />).</li>
                <li>Increase significance level (<InlineMath math="\alpha" />) (Note: this increases Type I error risk).</li>
                <li>The true parameter is far away from the null parameter (effect size is large).</li>
              </ol>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-8",
      title: "Topic 6.8: Confidence Intervals for the Difference of Two Proportions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Goal</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To estimate the difference between two population proportions (<InlineMath math="p_1 - p_2" />).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula (Two-Sample z-Interval)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="(\hat{p}_1 - \hat{p}_2) \pm z^* \sqrt{\frac{\hat{p}_1(1-\hat{p}_1)}{n_1} + \frac{\hat{p}_2(1-\hat{p}_2)}{n_2}}" />
            </div>
            <p className="text-slate-700"><strong>Standard Error:</strong> Note that the variances add under the radical.</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Random:</strong> Two independent random samples OR randomized assignment to two groups.</li>
              <li><strong>Independence (10%):</strong> <InlineMath math="n_1 \leq 0.10N_1" /> and <InlineMath math="n_2 \leq 0.10N_2" /> (applies if sampling without replacement).</li>
              <li><strong>Normal (Large Counts):</strong> Counts of successes and failures in <em>both</em> samples must be <InlineMath math="\geq 10" />.
                <div className="mt-2 bg-slate-100 p-2 rounded text-sm">
                  <InlineMath math="n_1\hat{p}_1, \, n_1(1-\hat{p}_1), \, n_2\hat{p}_2, \, n_2(1-\hat{p}_2)" /> all <InlineMath math="\geq 10" />
                </div>
              </li>
            </ol>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-9",
      title: "Topic 6.9: Justifying a Claim Based on a CI for Difference of Proportions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Interval</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="font-semibold text-green-900 mb-2">Template:</p>
              <p className="text-slate-700">"We are [C]% confident that the interval from [lower] to [upper] captures the true difference in proportions of [context group 1] minus [context group 2]."</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Checking for 0</h3>
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">If 0 is in the interval</h4>
                <p className="text-sm text-slate-700 mt-1">It is plausible that <InlineMath math="p_1 - p_2 = 0" />, meaning <InlineMath math="p_1 = p_2" />. There is <strong>no convincing evidence of a difference</strong>.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">If 0 is NOT in the interval</h4>
                <p className="text-sm text-slate-700 mt-1">It is not plausible that the proportions are equal. There is <strong>convincing evidence of a difference</strong>.</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li>If the entire interval is positive, <InlineMath math="p_1 > p_2" />.</li>
                  <li>If the entire interval is negative, <InlineMath math="p_1 < p_2" />.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-10",
      title: "Topic 6.10: Setting Up a Test for the Difference of Two Population Proportions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Hypotheses</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Null Hypothesis (<InlineMath math="H_0" />)</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="p_1 - p_2 = 0" /> OR <InlineMath math="p_1 = p_2" /> (Assume no difference).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Alternative Hypothesis (<InlineMath math="H_a" />)</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><InlineMath math="p_1 - p_2 \neq 0" /> (Two-tailed)</li>
                  <li><InlineMath math="p_1 - p_2 > 0" /> (One-tailed)</li>
                  <li><InlineMath math="p_1 - p_2 < 0" /> (One-tailed)</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions</h3>
            <p className="text-slate-700">
              Same as Topic 6.8 (Random, 10%, Large Counts). However, for the <strong>Test Statistic calculation only</strong> (Topic 6.11), we assume <InlineMath math="p_1 = p_2" />, which affects how we calculate the standard error.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-11",
      title: "Topic 6.11: Carrying Out a Test for the Difference of Two Population Proportions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Pooling (The Combined Proportion)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Since <InlineMath math="H_0" /> assumes the proportions are equal (<InlineMath math="p_1 = p_2" />), we combine the data from both samples to get a better estimate of the common proportion, called the <strong>pooled proportion</strong> (<InlineMath math="\hat{p}_c" />).
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="\hat{p}_c = \frac{x_1 + x_2}{n_1 + n_2} = \frac{\text{Total Successes}}{\text{Total Sample Size}}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Test Statistic Formula (Two-Sample z-Test)</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <BlockMath math="z = \frac{(\hat{p}_1 - \hat{p}_2) - 0}{\sqrt{\hat{p}_c(1-\hat{p}_c) \left(\frac{1}{n_1} + \frac{1}{n_2}\right)}}" />
            </div>
            <ImportantNote>
              The standard error uses the pooled proportion <InlineMath math="\hat{p}_c" />, not the individual sample proportions.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conclusion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Calculate the <InlineMath math="z" />-score, find the p-value using Table A or technology (2-PropZTest), compare to <InlineMath math="\alpha" />, and state the conclusion in context.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>P-value <InlineMath math="< \alpha" />:</strong> Reject <InlineMath math="H_0" />. Evidence of a difference.</li>
              <li><strong>P-value <InlineMath math="> \alpha" />:</strong> Fail to reject <InlineMath math="H_0" />. No evidence of a difference.</li>
            </ul>
          </section>
        </div>
      )
    }
  ]
};

export default statisticsUnit6Notes;
